// Fase 9 (Optimización) — compresión/redimensionado de imágenes *antes* de
// subirlas a Supabase Storage.
//
// Contexto: la sección 13 (Riesgos técnicos) del doc de arquitectura marca
// explícitamente "Crecimiento descontrolado de imágenes sin optimizar" como
// riesgo, con mitigación "Límites por plan + compresión automática
// obligatoria (sección 11.2)". Hasta la Fase 8, `almacenamiento.js` subía el
// archivo tal cual lo entregaba el input de archivo del navegador — una
// foto de celular moderna puede pesar 4-8 MB y medir miles de píxeles de
// lado, mucho más de lo que cualquier layout del storefront necesita.
//
// Esta capa reduce el peso en el origen (menos bytes subidos, menos bytes
// servidos luego a cada visitante de cada tienda) en vez de depender de un
// pipeline de transformación server-side, que no forma parte del stack
// actual (Supabase Storage no transforma imágenes en el plan usado por este
// proyecto). Es una mejora deliberadamente conservadora:
//
// - Nunca bloquea la subida: si el navegador no soporta Canvas/`toBlob` (o
//   la operación falla por cualquier motivo), se sube el archivo original
//   sin modificar — la compresión es una optimización, no un requisito.
// - Nunca sube un archivo más pesado que el original: si comprimir no
//   ayudó (imagen ya pequeña, formato ya eficiente), se conserva el
//   original.
// - Redimensiona solo si excede el máximo (nunca "estira" una imagen
//   pequeña).

/**
 * @typedef {object} OpcionesCompresion
 * @property {number} anchoMaximo
 * @property {number} altoMaximo
 * @property {number} calidad - 0 a 1
 */

/** Perfiles por caso de uso — ver justificación de cada tamaño en el punto
 * de llamada (subirImagenProducto/subirLogoEmpresa/subirImagenBanner). */
export const PERFIL_PRODUCTO = { anchoMaximo: 1600, altoMaximo: 1600, calidad: 0.82 };
export const PERFIL_BANNER = { anchoMaximo: 1920, altoMaximo: 1920, calidad: 0.8 };
export const PERFIL_LOGO = { anchoMaximo: 512, altoMaximo: 512, calidad: 0.9 };
// Fase 10 (Tematización visual) — el favicon se muestra a 16-32px reales;
// 128px es de sobra incluso para el ícono de "agregar a inicio" de un
// celular (192px reservado a apps instalables, fuera de alcance acá).
export const PERFIL_FAVICON = { anchoMaximo: 128, altoMaximo: 128, calidad: 0.9 };
// Categoría destacada — tarjeta mascota del home (aspecto ~16:11/4:5 con
// object-fit cover). El render real ocupa 250–400px de ancho; con 1200px de
// lado máximo cubre pantallas 2x y se conserva por debajo del peso del
// banner y del producto. Ver subirImagenCategoria.
export const PERFIL_CATEGORIA = { anchoMaximo: 1200, altoMaximo: 1200, calidad: 0.82 };

function soportaCompresion() {
  return (
    typeof document !== 'undefined' &&
    typeof document.createElement === 'function' &&
    typeof HTMLCanvasElement !== 'undefined' &&
    typeof HTMLCanvasElement.prototype.toBlob === 'function' &&
    typeof createImageBitmap === 'function'
  );
}

/**
 * @param {number} anchoOriginal
 * @param {number} altoOriginal
 * @param {number} anchoMaximo
 * @param {number} altoMaximo
 */
export function calcularDimensionesEscaladas(anchoOriginal, altoOriginal, anchoMaximo, altoMaximo) {
  if (anchoOriginal <= anchoMaximo && altoOriginal <= altoMaximo) {
    return { ancho: anchoOriginal, alto: altoOriginal };
  }

  const escala = Math.min(anchoMaximo / anchoOriginal, altoMaximo / altoOriginal);
  return {
    ancho: Math.max(1, Math.round(anchoOriginal * escala)),
    alto: Math.max(1, Math.round(altoOriginal * escala)),
  };
}

/**
 * Redimensiona y recomprime una imagen en el navegador. Devuelve siempre un
 * `File` válido: el comprimido si resultó más liviano, o el original si no
 * (o si el navegador no soporta la operación).
 *
 * @param {File} archivo
 * @param {OpcionesCompresion} opciones
 * @returns {Promise<File>}
 */
export async function comprimirImagen(archivo, opciones) {
  if (!archivo.type?.startsWith('image/') || !soportaCompresion()) {
    return archivo;
  }

  // SVG es vectorial: reescalarlo a raster solo lo empeoraría.
  if (archivo.type === 'image/svg+xml') return archivo;

  try {
    const bitmap = await createImageBitmap(archivo);
    const { ancho, alto } = calcularDimensionesEscaladas(
      bitmap.width,
      bitmap.height,
      opciones.anchoMaximo,
      opciones.altoMaximo,
    );

    const canvas = document.createElement('canvas');
    canvas.width = ancho;
    canvas.height = alto;
    const contexto = canvas.getContext('2d');
    if (!contexto) return archivo;

    contexto.drawImage(bitmap, 0, 0, ancho, alto);
    bitmap.close?.();

    // Preferir WebP (mejor relación peso/calidad); si el navegador no lo
    // soporta de verdad, `toBlob` cae a PNG y el chequeo de tamaño de abajo
    // evita subir un PNG más pesado que el JPEG/WEBP original.
    const tipoSalida = archivo.type === 'image/png' ? 'image/webp' : archivo.type;
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, tipoSalida, opciones.calidad),
    );

    if (!blob || blob.size >= archivo.size) return archivo;

    const extension = tipoSalida === 'image/webp' ? 'webp' : archivo.name.split('.').pop();
    const nombreBase = archivo.name.replace(/\.[^.]+$/, '');
    return new File([blob], `${nombreBase}.${extension}`, { type: tipoSalida });
  } catch {
    // Cualquier falla (imagen corrupta, memoria, navegador raro) degrada
    // sin romper el flujo de subida.
    return archivo;
  }
}
